"use client";

import { Slot } from "@frend-digital/ui";
import type {
  ComponentProps,
  MouseEventHandler,
  PropsWithChildren,
} from "react";
import { useAddToCart, useRemoveFromCart } from "../hooks/hooks";

export interface AddToCartProps extends ComponentProps<"button"> {
  itemId: string;
  onSuccess?: () => void;
}

/**
 * AddToCartTrigger component
 * @example
 * ```tsx
 * <AddToCartTrigger itemId={"123"} onSuccess={() => toast.success("Added to cart")}>
 *     <button>Add to cart</button>
 * </AddToCartTrigger>
 * ```
 */
export const AddToCartTrigger = ({
  children,
  itemId,
  onSuccess,
  onClick,
  ...rest
}: PropsWithChildren<AddToCartProps>) => {
  const mutation = useAddToCart({
    onSuccess,
  });

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onClick?.(e);
    mutation.mutate({ itemId });
  };
  return (
    <Slot data-pending={mutation.isPending} {...rest} onClick={handleClick}>
      {children}
    </Slot>
  );
};

export interface RemoveFromCartProps extends ComponentProps<"button"> {
  lineId: string;
  onSuccess?: () => void;
}

/**
 * RemoveFromCartTrigger component
 * @example
 * ```tsx
 * <RemoveFromCartTrigger itemId={"123"} onSuccess={() => toast.success("Removed from cart")}>
 *     <button>Remove from cart</button>
 * </RemoveFromCartTrigger>
 * ```
 */
export const RemoveFromCartTrigger = ({
  children,
  onSuccess,
  onClick,
  lineId,
}: RemoveFromCartProps) => {
  const mutation = useRemoveFromCart({
    onSuccess,
  });

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onClick?.(e);
    mutation.mutate({ line: lineId, optimistic: true });
  };

  return (
    <Slot data-pending={mutation.isPending} onClick={handleClick}>
      {children}
    </Slot>
  );
};
